/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import { motion } from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';

import { ArrowRightIcon } from '../../icons/Icons';
import { ANIMATIONS, INTERNAL_LINKS } from '../../utils';
import { Button, GradientHeader, Text } from '../design-system';
import { UseCasePreviewGrid } from '../use-cases/UseCasePreviewGrid';

const Wrapper = styled(motion.div)`
  display: flex;
  background: ${({ theme }) => theme.gradientFade};
  border-top: 1px solid ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.6)};
  padding: 100px 40px 120px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 80px 40px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    padding: 60px 30px;
  }
`;

const Inner = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 50px;
  max-width: 860px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    margin-bottom: 30px;
  }
`;

const Title = styled(GradientHeader).attrs({ renderAs: 'h1' })`
  padding: 0 20px 5px;
  && {
    margin-bottom: 0;
  }
`;

const Description = styled(Text).attrs({ size: 'extra-large' })`
  text-align: center;
  max-width: 730px;
  margin-bottom: 0;
`;

const Action = styled.div`
  margin-top: 15px;

  a {
    font-size: 16px;

    div:nth-child(2) {
      border-bottom: 1px solid ${({ theme }) => theme.colors.DEFAULT};
      transition: border-bottom 0.2s ease-in-out;
    }

    :hover {
      div:nth-child(2) {
        border-bottom: 1px solid transparent;
      }
    }
  }
`;

const DEFAULTS = {
  title: 'Build anything with data',
  description:
    'Get started with one of our many templates, from complex ML forecasting to critical business dashboards.',
  cta: 'Explore all templates'
};

interface UseCasesToutProps {
  cta?: string;
  description?: string;
  title?: string;
}

export const UseCasesTout: React.ComponentType<UseCasesToutProps> = React.memo(
  ({
    title = DEFAULTS.title,
    description = DEFAULTS.description,
    cta = DEFAULTS.cta
  }) => {
    return (
      <Wrapper {...ANIMATIONS.SimpleFadeIn}>
        <Inner>
          <Top>
            <Title renderAs="h1">{title}</Title>
            <Description>{description}</Description>
            <Action>
              <Button
                icon={<ArrowRightIcon />}
                iconPos="right"
                link={INTERNAL_LINKS.Templates}
                size="large"
                stylez="minimal"
                text={cta}
              />
            </Action>
          </Top>
          <UseCasePreviewGrid maxItems={6} />
        </Inner>
      </Wrapper>
    );
  }
);
